import * as React from 'react';
import { Styler, StylerBaseProps } from '../Styler';
import { useStyles } from './Stacker.styles';
import { ResponsiveStyleValue, SpacingValue } from '../../theming';
import { StackerDisplay, StackerDirection } from './Stacker.types';

export interface StackerProps extends StylerBaseProps {
    /**
     * The `display` css property.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default flex
     */
    display?: ResponsiveStyleValue<StackerDisplay>;

    /**
     * The `flex-direction` css property.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default column
     */
    direction?: ResponsiveStyleValue<StackerDirection>;

    /**
     * Sets the spacing that will applied between child items.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    spacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the horizontal spacing that will applied between child items. This will override `Spacing`.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    columnSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the vertical spacing that will applied between child items. This will override `Spacing`.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    rowSpacing?: ResponsiveStyleValue<SpacingValue>;
}

/**
 * Stacker is a simple utility component for layout, which displays a set of content as a stack. This can be stacked vertically on top of
 * one another, or horizontally in a row. Component allows customisation of the direction of the stack, and the spacing between the items, and also a choice
 * of display type. Stacker also extends Styler component which this allows us a great control over the component by controlling and customising the
 * styler properties responsively for each viewport if required.
 */
export function Stacker(props: Readonly<StackerProps>): React.JSX.Element {
    const {
        display = 'flex',
        direction = 'column',
        spacing = 1,
        columnSpacing: columnSpacingProp,
        rowSpacing: rowSpacingProp,
        ref,
        ...other
    } = props;

    const columnSpacing = columnSpacingProp ?? spacing;
    const rowSpacing = rowSpacingProp ?? spacing;

    const { styles } = useStyles({ columnSpacing, rowSpacing });

    return <Styler ref={ref} baseStyles={styles.root} display={display} flexDirection={direction} {...other} />;
}
